import React from "react";
import { ArrowUpRight, Anchor, Radio, Heart, Construction, Compass } from "lucide-react";
import { FLEET_DIRECTORY } from "../lib/fleetDirectory";

/**
 * FleetDirectory · Master Armada Roster on Landing
 *
 * Director directive 2026.06 · Strike #25. Renders below the Sovereignty
 * Tracks section and above the footer. 25D Obsidian/Gold aesthetic:
 * Deep Obsidian (#0A0A0A) canvas, Liquid Gold (#C9A227) borders and
 * accents, sharp-edged tactical cards with hover halos.
 *
 * Pending hulls are rendered with reduced opacity, a "COMING SOON" gold
 * pill, and `cursor-not-allowed` to remove any false-affordance.
 */

const CATEGORY_ICON = {
    primary:    Anchor,        // Six Sovereign Tracks
    operations: Radio,         // Lead Command, Signal Tower
    rescue:     Heart,         // Nessa, Mercy Properties
    pending:    Construction,  // Provisioning Queue
};

export default function FleetDirectory() {
    return (
        <section
            data-testid="fleet-directory"
            className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-20"
            style={{ background: "#0A0A0A" }}
        >
            {/* Eyebrow rail */}
            <div className="flex items-center gap-4 mb-8">
                <div className="h-px flex-1" style={{ background: "rgba(201,162,39,0.35)" }} />
                <div
                    className="text-[10px] uppercase tracking-[0.5em]"
                    style={{
                        color: "#C9A227",
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 800,
                    }}
                >
                    ◢ Master Directory · The Armada
                </div>
                <div className="h-px flex-1" style={{ background: "rgba(201,162,39,0.35)" }} />
            </div>

            {/* Section title */}
            <div className="text-center max-w-3xl mx-auto mb-14">
                <h2
                    data-testid="directory-headline"
                    className="font-heading tracking-tighter leading-[0.95] text-foreground"
                    style={{ fontSize: "clamp(2.25rem, 4.5vw, 4rem)" }}
                >
                    Every Platform.{" "}
                    <span className="gold-gradient-text italic">One Bridge.</span>
                </h2>
                <p
                    className="mt-5 text-base md:text-lg"
                    style={{ color: "#A9BCD2", fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                    The Flagship serves as the Master Directory for the entire EIGOO armada.
                    Tap any live node to engage; queued platforms light up as they ship.
                </p>
            </div>

            {/* Category sections */}
            <div className="space-y-12">
                {FLEET_DIRECTORY.map((cat) => (
                    <CategoryBlock key={cat.id} category={cat} />
                ))}
            </div>
        </section>
    );
}

function CategoryBlock({ category }) {
    const Icon = CATEGORY_ICON[category.id] || Compass;
    return (
        <div data-testid={`directory-category-${category.id}`}>
            {/* Category header */}
            <div className="flex items-center gap-3 mb-5">
                <div
                    className="inline-flex items-center justify-center w-9 h-9"
                    style={{
                        background: `${category.accent}14`,
                        border: `1px solid ${category.accent}55`,
                        color: category.accent,
                    }}
                >
                    <Icon size={16} strokeWidth={2} />
                </div>
                <div>
                    <div
                        className="text-sm md:text-base uppercase font-bold tracking-[0.25em]"
                        style={{
                            color: category.accent,
                            fontFamily: "'Montserrat', sans-serif",
                        }}
                    >
                        {category.title}
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "#5C7894" }}>
                        {category.subtitle}
                    </div>
                </div>
            </div>

            {/* Nodes grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {category.nodes.map((node) => (
                    <NodeCard key={node.slug} node={node} categoryAccent={category.accent} />
                ))}
            </div>
        </div>
    );
}

function NodeCard({ node, categoryAccent }) {
    const isPending = node.status === "pending";

    const baseStyle = {
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.08)",
        color: "#F8FAFC",
        opacity: isPending ? 0.55 : 1,
        cursor: isPending ? "not-allowed" : "pointer",
    };

    const content = (
        <>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span
                            className="text-base md:text-lg uppercase font-bold tracking-tight"
                            style={{
                                color: isPending ? "#5C7894" : "#C9A227",
                                fontFamily: "'Montserrat', sans-serif",
                            }}
                        >
                            {node.label}
                        </span>
                        {isPending && (
                            <span
                                className="text-[9px] uppercase tracking-[0.3em] px-2 py-0.5"
                                style={{
                                    color: "#C9A227",
                                    background: "rgba(201,162,39,0.08)",
                                    border: "1px solid #C9A22755",
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 700,
                                }}
                                data-testid={`directory-coming-soon-${node.slug}`}
                            >
                                Coming Soon
                            </span>
                        )}
                    </div>
                    <div className="mt-1 text-xs" style={{ color: "#829AB1" }}>
                        {node.tagline}
                    </div>
                    <div
                        className="mt-2 text-[10px] font-mono break-all"
                        style={{ color: "#5C7894" }}
                    >
                        {node.url.replace(/^https?:\/\//, "")}
                    </div>
                </div>
                {!isPending && (
                    <ArrowUpRight
                        size={16}
                        strokeWidth={2}
                        className="shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        style={{ color: "#C9A227" }}
                    />
                )}
            </div>
        </>
    );

    if (isPending) {
        return (
            <div
                data-testid={`directory-node-${node.slug}`}
                aria-disabled="true"
                className="group p-4 transition-all duration-300"
                style={baseStyle}
            >
                {content}
            </div>
        );
    }

    return (
        <a
            data-testid={`directory-node-${node.slug}`}
            href={node.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${node.label} · ${node.tagline}`}
            className="group p-4 transition-all duration-300 block"
            style={baseStyle}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#C9A227";
                e.currentTarget.style.background = "rgba(201,162,39,0.06)";
                e.currentTarget.style.boxShadow = "0 0 18px rgba(201,162,39,0.25)";
                e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                e.currentTarget.style.background = "rgba(255,255,255,0.02)";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "translateY(0)";
            }}
        >
            {content}
        </a>
    );
}
